/**
 * Google Calendar OAuth handshake for the extension side panel.
 *
 * Connect runs inside the side-panel iframe, but Google refuses to be framed,
 * so `connectGoogle()` opens a top-level popup. The popup completes OAuth and
 * the API redirects it back to `${origin}/app?google_connect=<result>`. These
 * helpers carry that result from the popup back to the iframe that opened it:
 *
 *  - `relayGoogleConnectIfPopup` runs at startup in the popup, forwards the
 *    result to its opener, and closes itself (so the popup never renders the
 *    full app).
 *  - `onGoogleConnectComplete` is used by the opener (IntegrationsTab) to react
 *    to that relay — refresh status + toast.
 *
 * window.opener.postMessage is the path that works in the extension: the popup
 * is a top-level window in a different storage partition than the iframe, which
 * BroadcastChannel cannot cross, but the live opener reference can. We post on
 * both for parity with the social-login flow (BroadcastChannel covers normal
 * same-partition tabs).
 */

export const GOOGLE_CONNECT_MESSAGE = "danny:google-connect-complete";
const CHANNEL = "danny-auth";

export type GoogleConnectResult = "success" | "error" | "send_enabled";

interface GoogleConnectMessage {
  type: typeof GOOGLE_CONNECT_MESSAGE;
  result: GoogleConnectResult;
}

function readResult(search: string): GoogleConnectResult | null {
  const v = new URLSearchParams(search).get("google_connect");
  return v === "success" || v === "error" || v === "send_enabled" ? v : null;
}

function isGoogleConnectMessage(data: unknown): data is GoogleConnectMessage {
  return (
    !!data &&
    typeof data === "object" &&
    (data as { type?: unknown }).type === GOOGLE_CONNECT_MESSAGE
  );
}

/**
 * If this window is the OAuth popup (it has an opener and the google_connect
 * param), relay the result to the opener and close. Returns true when handled,
 * so the caller can skip rendering the app in the popup.
 */
export function relayGoogleConnectIfPopup(win: Window = window): boolean {
  const result = readResult(win.location.search);
  if (!result) return false;
  const isPopup = !!win.opener && win.opener !== win;
  if (!isPopup) return false;

  const payload: GoogleConnectMessage = { type: GOOGLE_CONNECT_MESSAGE, result };
  try {
    const bc = new BroadcastChannel(CHANNEL);
    bc.postMessage(payload);
    bc.close();
  } catch {
    // BroadcastChannel unsupported — opener.postMessage below still covers it.
  }
  try {
    (win.opener as Window).postMessage(payload, win.location.origin);
  } catch {
    // opener gone or cross-origin — best effort.
  }
  win.close();
  return true;
}

/**
 * Subscribe to google-connect-complete relays from the popup. Returns an
 * unsubscribe function. Used by the window that opened the popup.
 */
export function onGoogleConnectComplete(
  cb: (result: GoogleConnectResult) => void,
  win: Window = window,
): () => void {
  const onMessage = (e: MessageEvent) => {
    if (e.origin !== win.location.origin) return;
    if (isGoogleConnectMessage(e.data)) cb(e.data.result);
  };
  win.addEventListener("message", onMessage);

  let bc: BroadcastChannel | null = null;
  try {
    bc = new BroadcastChannel(CHANNEL);
    bc.onmessage = (e) => {
      if (isGoogleConnectMessage(e.data)) cb(e.data.result);
    };
  } catch {
    // BroadcastChannel unsupported — the postMessage listener still works.
  }

  return () => {
    win.removeEventListener("message", onMessage);
    bc?.close();
  };
}
